// The respondent-facing form: one card per numbered question, laid out like the
// printed evaluation sheet the academy already hands out on paper.
//
// Conditional questions ("Si oui, lesquelles ?") deliberately do not get a card
// or a number of their own — they render inside the card of the question they
// depend on, which is how the paper sheet reads and keeps the numbering stable
// whether or not the follow-up is showing.

import { useMutation } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { ForwardArrow } from "#/components/ui";
import { useI18n } from "#/lib/i18n";
import {
	type Answers,
	type AnswerValue,
	FORM_STRINGS,
	groupQuestions,
	isVisible,
	localized,
	type Question,
	type QuestionGroup,
	type QuestionnaireCopy,
	usableChoices,
	validateAnswers,
} from "#/lib/questionnaire";

/* -------------------------------------------------------------------------- */
/*  Form                                                                      */
/* -------------------------------------------------------------------------- */

export function QuestionnaireForm({
	questionnaireId,
	questions,
	copy,
	collectIdentity,
}: {
	questionnaireId: string;
	questions: Question[];
	copy: QuestionnaireCopy;
	collectIdentity: boolean;
}) {
	const { locale, dir } = useI18n();
	const { trpc } = useRouteContext({ from: "__root__" });
	const strings = FORM_STRINGS[locale];

	const [answers, setAnswers] = useState<Answers>({});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [identity, setIdentity] = useState({ name: "", email: "", phone: "" });
	const [website, setWebsite] = useState(""); // honeypot
	const formRef = useRef<HTMLFormElement>(null);

	const mutation = useMutation(trpc.questionnaires.submit.mutationOptions());

	const groups = useMemo(() => groupQuestions(questions), [questions]);

	function setAnswer(questionId: string, value: AnswerValue) {
		setAnswers((current) => ({ ...current, [questionId]: value }));
		// Clear the field's error as soon as it's touched — re-validation happens
		// on submit, so leaving a stale red border would be misleading.
		setErrors((current) => {
			if (!(questionId in current)) return current;
			const next = { ...current };
			delete next[questionId];
			return next;
		});
	}

	function handleSubmit(event: React.FormEvent) {
		event.preventDefault();
		if (mutation.isPending) return;

		const result = validateAnswers(questions, answers, locale);
		setErrors(result.errors);

		const firstBad = Object.keys(result.errors)[0];
		if (firstBad) {
			formRef.current
				?.querySelector(`[data-question="${firstBad}"]`)
				?.scrollIntoView({ behavior: "smooth", block: "center" });
			return;
		}

		mutation.mutate({
			questionnaireId,
			locale,
			answers: result.answers,
			...(collectIdentity
				? {
						respondentName: identity.name.trim() || undefined,
						respondentEmail: identity.email.trim() || undefined,
						respondentPhone: identity.phone.trim() || undefined,
					}
				: {}),
			website: website || undefined,
		});
	}

	if (mutation.isSuccess) {
		return (
			<div className="mx-auto flex max-w-xl animate-fade-up flex-col items-center gap-4 rounded-3xl bg-white px-6 py-14 text-center shadow-card ring-1 ring-line">
				<span className="flex h-14 w-14 items-center justify-center rounded-full bg-teal text-white">
					<Check className="h-7 w-7" />
				</span>
				<h2 className="font-display text-2xl font-bold text-navy">
					{copy.thankYouTitle.trim() || strings.defaultThankYouTitle}
				</h2>
				<p className="max-w-sm text-sm leading-relaxed text-muted">
					{copy.thankYouText.trim() || strings.defaultThankYouText}
				</p>
			</div>
		);
	}

	return (
		<form
			ref={formRef}
			onSubmit={handleSubmit}
			noValidate
			className="flex flex-col gap-5"
		>
			{collectIdentity ? (
				<IdentityCard
					strings={strings}
					value={identity}
					onChange={setIdentity}
				/>
			) : null}

			{groups.map((group, index) => (
				<QuestionCard
					key={group.lead.id}
					group={group}
					index={index + 1}
					answers={answers}
					errors={errors}
					onAnswer={setAnswer}
				/>
			))}

			{/* honeypot — hidden from real visitors, bots tend to fill every input */}
			<input
				type="text"
				name="website"
				value={website}
				onChange={(e) => setWebsite(e.target.value)}
				tabIndex={-1}
				autoComplete="off"
				aria-hidden="true"
				className="absolute left-[-9999px] h-0 w-0 opacity-0"
			/>

			<div className="flex flex-col items-center gap-3 pt-2">
				{Object.keys(errors).length > 0 ? (
					<p className="text-sm font-semibold text-rose-600">
						{strings.errorSummary}
					</p>
				) : null}
				{mutation.isError ? (
					<p className="text-sm font-semibold text-rose-600">
						{mutation.error.message}
					</p>
				) : null}

				<button
					type="submit"
					disabled={mutation.isPending}
					className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-teal-700 px-9 py-4 text-base font-bold text-white shadow-glow transition-all duration-200 hover:-translate-y-0.5 hover:bg-teal-600 active:translate-y-0 active:scale-[0.985] disabled:pointer-events-none disabled:opacity-70"
				>
					{mutation.isPending ? (
						<>
							<Loader2 className="h-5 w-5 animate-spin" />
							{strings.submitting}
						</>
					) : (
						<>
							{copy.submitLabel.trim() || strings.defaultSubmit}
							<ForwardArrow
								className={`h-4 w-4 transition-transform ${
									dir === "rtl"
										? "group-hover:-translate-x-1"
										: "group-hover:translate-x-1"
								}`}
							/>
						</>
					)}
				</button>

				<p className="text-xs text-muted">{strings.requiredNote}</p>
			</div>
		</form>
	);
}

/* -------------------------------------------------------------------------- */
/*  Cards                                                                     */
/* -------------------------------------------------------------------------- */

const IDENTITY_FIELD_CLASS =
	"w-full rounded-xl border-0 bg-canvas px-4 py-3 text-sm text-ink outline-none ring-1 ring-line transition-shadow placeholder:text-muted/60 focus:ring-2 focus:ring-teal";

function IdentityCard({
	strings,
	value,
	onChange,
}: {
	strings: (typeof FORM_STRINGS)["ar"];
	value: { name: string; email: string; phone: string };
	onChange: (next: { name: string; email: string; phone: string }) => void;
}) {
	return (
		<section className="rounded-3xl bg-white p-5 shadow-card ring-1 ring-line sm:p-7">
			<h2 className="mb-4 font-display text-base font-bold text-navy">
				{strings.identityHeading}{" "}
				<span className="text-xs font-semibold text-muted">
					({strings.optional})
				</span>
			</h2>
			<div className="grid gap-3 sm:grid-cols-3">
				<input
					type="text"
					autoComplete="name"
					value={value.name}
					onChange={(e) => onChange({ ...value, name: e.target.value })}
					placeholder={strings.identityName}
					aria-label={strings.identityName}
					className={IDENTITY_FIELD_CLASS}
				/>
				<input
					type="email"
					inputMode="email"
					autoComplete="email"
					dir="ltr"
					value={value.email}
					onChange={(e) => onChange({ ...value, email: e.target.value })}
					placeholder={strings.identityEmail}
					aria-label={strings.identityEmail}
					className={`${IDENTITY_FIELD_CLASS} text-start`}
				/>
				<input
					type="tel"
					inputMode="tel"
					autoComplete="tel"
					dir="ltr"
					value={value.phone}
					onChange={(e) => onChange({ ...value, phone: e.target.value })}
					placeholder={strings.identityPhone}
					aria-label={strings.identityPhone}
					className={`${IDENTITY_FIELD_CLASS} text-start`}
				/>
			</div>
		</section>
	);
}

function QuestionCard({
	group,
	index,
	answers,
	errors,
	onAnswer,
}: {
	group: QuestionGroup;
	index: number;
	answers: Answers;
	errors: Record<string, string>;
	onAnswer: (questionId: string, value: AnswerValue) => void;
}) {
	const { locale } = useI18n();
	const { lead, followUps } = group;
	const label = localized(lead.label, locale);
	const help = localized(lead.help, locale);
	const error = errors[lead.id];

	return (
		<section
			data-question={lead.id}
			className={`rounded-3xl bg-white p-5 shadow-card ring-1 transition-colors sm:p-7 ${
				error ? "ring-rose-300" : "ring-line"
			}`}
		>
			<h2 className="font-display text-base font-bold leading-relaxed text-navy sm:text-lg">
				<span className="index-num text-teal">
					{String(index).padStart(2, "0")}
				</span>
				<span aria-hidden className="mx-2 text-teal/60">
					–
				</span>
				{label}
				{lead.required ? (
					<span className="ms-1 text-rose-500" aria-hidden>
						*
					</span>
				) : null}
			</h2>

			{help ? (
				<p className="mt-1.5 text-sm leading-relaxed text-muted">{help}</p>
			) : null}

			<div className="mt-4">
				<AnswerField
					question={lead}
					value={answers[lead.id]}
					onChange={(value) => onAnswer(lead.id, value)}
				/>
			</div>

			{error ? (
				<p className="mt-2.5 text-sm font-semibold text-rose-600">{error}</p>
			) : null}

			{followUps.map((question) =>
				isVisible(question, answers) ? (
					<FollowUp
						key={question.id}
						question={question}
						value={answers[question.id]}
						error={errors[question.id]}
						onChange={(value) => onAnswer(question.id, value)}
					/>
				) : null,
			)}
		</section>
	);
}

/** A conditional question, inside its parent's card and without a number. */
function FollowUp({
	question,
	value,
	error,
	onChange,
}: {
	question: Question;
	value: AnswerValue | undefined;
	error?: string;
	onChange: (value: AnswerValue) => void;
}) {
	const { locale } = useI18n();
	const help = localized(question.help, locale);

	return (
		<div
			data-question={question.id}
			className="mt-6 animate-fade-up border-t border-line pt-5"
		>
			<p className="text-sm font-bold text-teal-700">
				{localized(question.label, locale)}
				{question.required ? (
					<span className="ms-1 text-rose-500" aria-hidden>
						*
					</span>
				) : null}
			</p>
			{help ? <p className="mt-1 text-sm text-muted">{help}</p> : null}
			<div className="mt-3">
				<AnswerField question={question} value={value} onChange={onChange} />
			</div>
			{error ? (
				<p className="mt-2 text-sm font-semibold text-rose-600">{error}</p>
			) : null}
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Fields                                                                    */
/* -------------------------------------------------------------------------- */

const TEXT_FIELD_CLASS =
	"w-full rounded-xl border-0 bg-canvas px-4 py-3 text-base text-ink outline-none ring-1 ring-line transition-shadow placeholder:text-muted/50 focus:ring-2 focus:ring-teal";

function AnswerField({
	question,
	value,
	onChange,
}: {
	question: Question;
	value: AnswerValue | undefined;
	onChange: (value: AnswerValue) => void;
}) {
	const { locale } = useI18n();
	const placeholder = localized(question.placeholder, locale);

	switch (question.type) {
		case "long_text":
			return (
				<textarea
					rows={4}
					value={typeof value === "string" ? value : ""}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					aria-label={localized(question.label, locale)}
					className={`${TEXT_FIELD_CLASS} resize-y leading-relaxed`}
				/>
			);

		case "short_text":
		case "email":
		case "phone":
		case "number":
		case "date": {
			const type =
				question.type === "short_text"
					? "text"
					: question.type === "phone"
						? "tel"
						: question.type;
			// Latin-only values read wrong inside an RTL field, so they get their
			// own direction regardless of the page's.
			const ltr = ["email", "phone", "number", "date"].includes(question.type);
			return (
				<input
					type={type}
					inputMode={
						question.type === "number"
							? "numeric"
							: question.type === "phone"
								? "tel"
								: question.type === "email"
									? "email"
									: undefined
					}
					dir={ltr ? "ltr" : undefined}
					value={typeof value === "string" ? value : ""}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					aria-label={localized(question.label, locale)}
					className={`${TEXT_FIELD_CLASS} ${ltr ? "text-start" : ""}`}
				/>
			);
		}

		case "dropdown":
			return (
				<select
					value={typeof value === "string" ? value : ""}
					onChange={(e) => onChange(e.target.value)}
					aria-label={localized(question.label, locale)}
					className={`${TEXT_FIELD_CLASS} appearance-none ${
						value ? "text-ink" : "text-muted/60"
					}`}
				>
					<option value="">{placeholder || "—"}</option>
					{usableChoices(question.options).map((option) => (
						<option key={option.id} value={option.id} className="text-ink">
							{localized(option.label, locale)}
						</option>
					))}
				</select>
			);

		case "single_choice":
			return (
				<ChoiceList
					question={question}
					selected={typeof value === "string" && value ? [value] : []}
					multiple={false}
					onToggle={(id) => onChange(id)}
				/>
			);

		case "multi_choice":
			return (
				<ChoiceList
					question={question}
					selected={Array.isArray(value) ? value : []}
					multiple
					onToggle={(id) => {
						const current = Array.isArray(value) ? value : [];
						onChange(
							current.includes(id)
								? current.filter((v) => v !== id)
								: [...current, id],
						);
					}}
				/>
			);

		case "scale":
			return (
				<ScaleField
					question={question}
					value={typeof value === "string" ? value : ""}
					onChange={onChange}
				/>
			);

		case "grid":
			return (
				<GridField
					question={question}
					value={
						value && typeof value === "object" && !Array.isArray(value)
							? value
							: {}
					}
					onChange={onChange}
				/>
			);

		default:
			return null;
	}
}

/**
 * Radio / checkbox list. Short option sets sit on one line (Oui / Non on the
 * paper sheet); anything longer stacks, because wrapped choice pills are very
 * easy to mis-tap.
 */
function ChoiceList({
	question,
	selected,
	multiple,
	onToggle,
}: {
	question: Question;
	selected: string[];
	multiple: boolean;
	onToggle: (id: string) => void;
}) {
	const { locale } = useI18n();
	// Unlabelled choices are dropped rather than drawn as blank rows — see
	// usableChoices(); the validator applies the same rule.
	const options = usableChoices(question.options);
	const labels = options.map((o) => localized(o.label, locale));
	const inline = options.length <= 3 && labels.every((l) => l.length <= 24);

	return (
		// A fieldset rather than a div with a role: it names the group for a screen
		// reader without repeating the question text on screen, and works the same
		// for the radio and the checkbox case.
		<fieldset
			className={
				inline ? "flex flex-wrap gap-x-10 gap-y-3" : "flex flex-col gap-2.5"
			}
		>
			<legend className="sr-only">{localized(question.label, locale)}</legend>
			{options.map((option, index) => {
				const isSelected = selected.includes(option.id);
				return (
					<label
						key={option.id}
						className="group flex cursor-pointer items-center gap-3 text-sm text-ink sm:text-base"
					>
						<input
							type={multiple ? "checkbox" : "radio"}
							name={question.id}
							value={option.id}
							checked={isSelected}
							onChange={() => onToggle(option.id)}
							className="peer sr-only"
						/>
						{/* The control itself is sr-only, so the focus ring has to be
						    painted on this box — without it a keyboard user moving
						    through the options gets no visible cursor at all. */}
						<span
							aria-hidden
							className={`flex h-5.5 w-5.5 flex-none items-center justify-center border-2 transition-all peer-focus-visible:ring-2 peer-focus-visible:ring-teal peer-focus-visible:ring-offset-2 ${
								multiple ? "rounded-md" : "rounded-full"
							} ${
								isSelected
									? "border-teal bg-teal text-white"
									: "border-line bg-white group-hover:border-teal/50"
							}`}
						>
							{isSelected ? (
								multiple ? (
									<Check className="h-3.5 w-3.5" strokeWidth={3} />
								) : (
									<span className="h-2 w-2 rounded-full bg-white" />
								)
							) : null}
						</span>
						<span className={isSelected ? "font-semibold text-navy" : ""}>
							{labels[index]}
						</span>
					</label>
				);
			})}
		</fieldset>
	);
}

/** Numbered circles, 01 … 10 — the overall-satisfaction row on the paper sheet. */
function ScaleField({
	question,
	value,
	onChange,
}: {
	question: Question;
	value: string;
	onChange: (value: string) => void;
}) {
	const { locale } = useI18n();
	const strings = FORM_STRINGS[locale];
	const min = Math.min(question.scaleMin, question.scaleMax);
	const max = Math.max(question.scaleMin, question.scaleMax);
	const points = Array.from({ length: max - min + 1 }, (_, i) => min + i);
	const lowLabel = localized(question.scaleMinLabel, locale);
	const highLabel = localized(question.scaleMaxLabel, locale);

	return (
		<div className="flex flex-col gap-2">
			<fieldset className="flex flex-wrap gap-2">
				<legend className="sr-only">{localized(question.label, locale)}</legend>
				{points.map((point) => {
					const isSelected = value === String(point);
					return (
						<label key={point} className="cursor-pointer" title={`${point}`}>
							<input
								type="radio"
								name={question.id}
								value={point}
								checked={isSelected}
								onChange={() => onChange(String(point))}
								className="peer sr-only"
							/>
							<span
								className={`index-num flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold transition-all peer-focus-visible:ring-2 peer-focus-visible:ring-teal peer-focus-visible:ring-offset-2 ${
									isSelected
										? "border-teal bg-teal text-white shadow-glow"
										: "border-line bg-white text-muted hover:border-teal/50 hover:text-teal"
								}`}
							>
								{String(point).padStart(2, "0")}
							</span>
						</label>
					);
				})}
			</fieldset>
			{lowLabel || highLabel ? (
				<div className="flex justify-between text-xs font-semibold text-muted">
					<span>{lowLabel || strings.scaleLow}</span>
					<span>{highLabel || strings.scaleHigh}</span>
				</div>
			) : null}
		</div>
	);
}

/**
 * The rating matrix. Rendered twice: a real table from `sm` up, and a stacked
 * card-per-criterion below it — a 5-column table on a 390px phone is unusable,
 * and horizontal scrolling hides the very columns being compared.
 */
function GridField({
	question,
	value,
	onChange,
}: {
	question: Question;
	value: Record<string, string>;
	onChange: (value: Record<string, string>) => void;
}) {
	const { locale } = useI18n();
	const rows = usableChoices(question.rows);
	const columns = usableChoices(question.columns);

	function pick(rowId: string, columnId: string) {
		onChange({ ...value, [rowId]: columnId });
	}

	/**
	 * Both layouts are in the DOM at once (only CSS hides one), so their radios
	 * must not share a group name. With one name across both, checking a cell
	 * re-renders *both* copies as checked, and the browser's radio-group rule
	 * then unchecks whichever React writes first — leaving the visible input
	 * reporting unchecked to screen readers and breaking arrow-key navigation,
	 * even though the answer and the styling look right.
	 */
	const groupName = (rowId: string, layout: "d" | "m") =>
		`${question.id}:${rowId}:${layout}`;

	// A grid the admin hasn't finished (no criteria yet, or no ratings to pick
	// from) renders as nothing rather than as a bare header row over an empty
	// table — the question's own label and help text still show above.
	if (rows.length === 0 || columns.length === 0) return null;

	return (
		<>
			<div className="hidden overflow-x-auto sm:block">
				<table className="w-full border-collapse text-start">
					<thead>
						<tr>
							{/* Empty corner cell — the criteria column needs no heading. */}
							<th className="pb-3 pe-4 text-start text-sm font-bold text-teal-700" />
							{columns.map((column) => (
								<th
									key={column.id}
									className="px-2 pb-3 text-center text-sm font-bold text-teal-700"
								>
									{localized(column.label, locale)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr key={row.id} className="border-t border-line/70">
								<th
									scope="row"
									className="py-2.5 pe-4 text-start text-sm font-normal leading-snug text-ink"
								>
									{localized(row.label, locale)}
								</th>
								{columns.map((column) => {
									const isSelected = value[row.id] === column.id;
									return (
										<td key={column.id} className="px-2 py-2.5 text-center">
											<label className="inline-flex cursor-pointer">
												<input
													type="radio"
													name={groupName(row.id, "d")}
													value={column.id}
													checked={isSelected}
													onChange={() => pick(row.id, column.id)}
													aria-label={`${localized(row.label, locale)} — ${localized(column.label, locale)}`}
													className="peer sr-only"
												/>
												<span
													aria-hidden
													className={`block h-6 w-14 rounded-full border-2 transition-all peer-focus-visible:ring-2 peer-focus-visible:ring-teal peer-focus-visible:ring-offset-2 ${
														isSelected
															? "border-teal bg-teal"
															: "border-line bg-white hover:border-teal/50"
													}`}
												/>
											</label>
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="flex flex-col gap-4 sm:hidden">
				{rows.map((row) => (
					<fieldset key={row.id} className="border-t border-line/70 pt-3">
						<legend className="mb-2 text-sm font-semibold text-ink">
							{localized(row.label, locale)}
						</legend>
						<div className="flex flex-wrap gap-2">
							{columns.map((column) => {
								const isSelected = value[row.id] === column.id;
								return (
									<label key={column.id} className="cursor-pointer">
										<input
											type="radio"
											name={groupName(row.id, "m")}
											value={column.id}
											checked={isSelected}
											onChange={() => pick(row.id, column.id)}
											className="peer sr-only"
										/>
										<span
											className={`inline-flex rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition-all peer-focus-visible:ring-2 peer-focus-visible:ring-teal peer-focus-visible:ring-offset-2 ${
												isSelected
													? "border-teal bg-teal text-white"
													: "border-line bg-white text-muted"
											}`}
										>
											{localized(column.label, locale)}
										</span>
									</label>
								);
							})}
						</div>
					</fieldset>
				))}
			</div>
		</>
	);
}
